import { useState, useRef, useEffect } from 'react'
import { Popover, ActionIcon } from '@mantine/core'
import { useMutation } from '@apollo/client/react'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import AddMusicReaction from '../graphql/queries/AddMusicReaction'

export default function TrackLinkPopover({ track, roomId }) {
    const dispatch = useAppDispatch()
    const [opened, setOpened] = useState(false)
    const [addMusicReaction] = useMutation(AddMusicReaction)
    const hoverTimeoutRef = useRef(null)
    const closeTimeoutRef = useRef(null)

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current)
            }
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current)
            }
        }
    }, [])

    // Safety check for track
    if (!track || !track.title) {
        return <span className="TrackLink"></span>
    }

    function handleMouseEnter() {
        // Cancel any pending close timeout
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
        }

        // Start a timer to open the popover after 1 second
        hoverTimeoutRef.current = setTimeout(() => {
            setOpened(true)
        }, 1000)
    }

    function handleMouseLeave() {
        // Clear the timer if the user moves away before 1 second
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
            hoverTimeoutRef.current = null
        }

        // If the popover is open, start a timer to close it after 1 second
        if (opened) {
            closeTimeoutRef.current = setTimeout(() => {
                setOpened(false)
            }, 1000)
        }
    }

    function handlePopoverMouseEnter() {
        // Cancel any pending close timeout when hovering over the popover
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
        }
    }

    function handlePopoverMouseLeave() {
        // Start a timer to close the popover after 1 second
        closeTimeoutRef.current = setTimeout(() => {
            setOpened(false)
        }, 1000)
    }

    async function handleReaction(reactionType) {
        setOpened(false)
        try {
            await addMusicReaction({
                variables: {
                    roomId,
                    trackId: track.id,
                    reaction: reactionType
                }
            })
        } catch (error) {
            console.error('failed to add reaction', error)
        }
    }

    function handleShowTrackInfo() {
        setOpened(false)
        dispatch(Actions.showTrackModal(track.id))
    }

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position="top"
            withArrow
            shadow="md"
        >
            <Popover.Target>
                <span
                    className="TrackLink"
                    onClick={() => setOpened(true)}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    style={{ cursor: 'pointer' }}
                >
                    {track.title}
                </span>
            </Popover.Target>
            <Popover.Dropdown
                onMouseEnter={handlePopoverMouseEnter}
                onMouseLeave={handlePopoverMouseLeave}
                style={{
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
                    border: '1px solid #d0d7de',
                    borderRadius: '8px',
                    padding: '8px'
                }}
                styles={{
                    arrow: {
                        background: '#f5f7fa',
                        border: '1px solid #d0d7de'
                    }
                }}
            >
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                }}>
                    <ActionIcon
                        variant="subtle"
                        color="dark"
                        size="lg"
                        onClick={() => handleReaction('thumbsUp')}
                        title="Thumbs up"
                    >
                        <span className="material-symbols-outlined">thumb_up</span>
                    </ActionIcon>
                    <ActionIcon
                        variant="subtle"
                        color="dark"
                        size="lg"
                        onClick={() => handleReaction('thumbsDown')}
                        title="Thumbs down"
                    >
                        <span className="material-symbols-outlined">thumb_down</span>
                    </ActionIcon>
                    <ActionIcon
                        variant="subtle"
                        color="dark"
                        size="lg"
                        onClick={handleShowTrackInfo}
                        title="Track info"
                    >
                        <span className="material-symbols-outlined">article</span>
                    </ActionIcon>
                </div>
            </Popover.Dropdown>
        </Popover>
    )
}

